import type { IconName } from '@repo/design-system';
import type {
  FloorPlannerDraftBasis,
  TwinLaunchHistoryResponse,
  TwinLaunchSummary,
} from '@repo/frontend-types';
import {
  PLAN_KIND_STEP,
  PLAN_STEP_IDS,
  type OnboardingRoute,
  type PlanComparison,
  type PlanKind,
  type PlanStepId,
} from '@repo/front-end-utils';

import { whenText } from './draftChoiceText';
import { STEPS } from './onboardingSteps';
import type { ChangesState } from './VersionsModal';

/*
 * The words Versions says (`twin-details-page-versions`, R1–R4) — pure, so every reading of the chip,
 * the draft header and the change pills is testable without rendering — and the Load confirmation's
 * (`twin-details-page-load-launch`, R1–R3, R6), whose by-step list is `launchDifference`.
 */

/**
 * The launch number a draft is "from" (R1.2): the launch it was loaded from, else the launch it was
 * based on, else production. `null` when that launch is not in the history (pruned) or has no
 * number (an older middleware sends none, so `launchNumber` is read with `== null`).
 */
export const draftOriginNumber = (
  draftBasis: FloorPlannerDraftBasis | null | undefined,
  history: TwinLaunchHistoryResponse,
): number | null => {
  const candidates = [
    draftBasis?.loadedFromLaunch,
    draftBasis?.basedOnLaunch,
    history.productionLaunchUuid,
  ];
  for (const uuid of candidates) {
    if (!uuid) continue;
    const launch = history.launches.find(l => l.launchUuid === uuid);
    if (!launch) return null;
    return launch.launchNumber ?? null;
  }
  return null;
};

/**
 * Where the draft comes from, once for the chip and the header (R1.2, R3.1): not launched (a new or
 * migrating twin), unknown (the launch record is unread), or a launch number — `null` for a launch
 * the history no longer lists or that has no number.
 */
export type DraftOrigin = 'not-launched' | 'unknown' | number | null;
export const draftOrigin = (args: {
  route: OnboardingRoute;
  draftBasis: FloorPlannerDraftBasis | null | undefined;
  history: TwinLaunchHistoryResponse | null;
}): DraftOrigin => {
  const { route, draftBasis, history } = args;
  if (route === 'create' || route === 'migrate') return 'not-launched';
  if (route === 'live-unread' || !history) return 'unknown';
  return draftOriginNumber(draftBasis, history);
};

/**
 * The chip's version text (R1.2): after the route label, or `null` for none. A twin with no history
 * read yet (`history` null) on a `manage` route says nothing rather than guess a number.
 */
export const versionText = (args: {
  route: OnboardingRoute;
  draftBasis: FloorPlannerDraftBasis | null | undefined;
  history: TwinLaunchHistoryResponse | null;
}): string | null => {
  const origin = draftOrigin(args);
  if (origin === 'not-launched') return 'Draft · not launched';
  if (origin === 'unknown') return null;
  return origin === null ? 'Draft from an earlier launch' : `Draft from v${origin}`;
};

/** The draft header's title (R3.1). */
export const draftTitle = (args: {
  route: OnboardingRoute;
  draftBasis: FloorPlannerDraftBasis | null | undefined;
  history: TwinLaunchHistoryResponse | null;
}): string => {
  const origin = draftOrigin(args);
  if (origin === 'not-launched') return 'Your draft · not launched';
  if (origin === 'unknown') return 'Your draft';
  return origin === null ? 'Your draft, from an earlier launch' : `Your draft, from v${origin}`;
};

/**
 * *Discard Draft* is offered (R3.3; epic question 8): only on a launched twin's draft that holds
 * unlaunched work. A new or migrating twin has no production to discard to, an unread launch record
 * names none, and a draft equal to production has nothing to discard.
 */
export const canDiscardDraft = (args: {
  route: OnboardingRoute | null;
  hasProduction: boolean;
  hasUnlaunched: boolean;
}): boolean => args.route === 'manage' && args.hasProduction && args.hasUnlaunched;

/**
 * *Discard Draft*'s hover and focus tip, and its accessible description (`remove-draft-choice-modal`,
 * R3): the button is the way back to production now the opening question is gone.
 */
export const DISCARD_HINT =
  'Discards any drafted changes and reverts to the last production release.';

/** The signed-in person, ignoring case — said as *you* (R3.2) or *You* (R5). */
export const isCurrentUser = (
  email: string | null | undefined,
  current: string | null | undefined,
): boolean => !!email && !!current && email.toLowerCase() === current.toLowerCase();

/** The draft header's sub-line (R3.2): *Last saved {when} by {who} · {state}*. */
export const draftSubline = (args: {
  savedAt: string | null;
  savedBy: string | null;
  currentEmail: string | null | undefined;
  notLaunched: boolean;
  /**
   * Production was read, so "same as production" can be said. While the history is unread a draft
   * with nothing marked unlaunched is not known to equal anything, and no state is claimed.
   */
  productionKnown?: boolean;
}): string => {
  const { savedAt, savedBy, currentEmail, notLaunched, productionKnown = true } = args;
  const state = notLaunched ? 'not launched' : productionKnown ? 'same as production' : null;
  const tail = state ? ` · ${state}` : '';
  if (!savedAt) return `Not saved yet${tail}`;
  const who = !savedBy ? null : isCurrentUser(savedBy, currentEmail) ? 'you' : savedBy;
  return `Last saved ${whenText(savedAt)}${who ? ` by ${who}` : ''}${tail}`;
};

/** What each plan kind is called in a pill, singular then plural (R4.3). */
const KIND_NOUN: Record<PlanKind, [string, string]> = {
  floors: ['floor', 'floors'],
  zones: ['zone', 'zones'],
  placedEquipment: ['asset', 'assets'],
  cameras: ['camera', 'cameras'],
  observers: ['observer', 'observers'],
  compositeMeasures: ['measure', 'measures'],
};

/** Kinds in the order a step's pill lists them: Floor Plan names floors before zones. */
const KIND_ORDER: readonly PlanKind[] = [
  'floors',
  'zones',
  'placedEquipment',
  'cameras',
  'observers',
  'compositeMeasures',
];

const STEP_TITLE: Record<PlanStepId, string> = Object.fromEntries(
  STEPS.map(step => [step.id, step.title]),
) as Record<PlanStepId, string>;

export interface ChangePill {
  step: PlanStepId;
  /** The step's title, as the step list names it. */
  label: string;
  /** What changed there, e.g. *2 floors changed · 1 zone added*. */
  text: string;
}

const noun = (n: number, kind: PlanKind) => KIND_NOUN[kind][n === 1 ? 0 : 1];
const phrase = (n: number, kind: PlanKind, verb: 'added' | 'removed' | 'changed') =>
  `${n} ${noun(n, kind)} ${verb}`;

/**
 * One pill per step with a change, in the wizard's step order (`PLAN_STEP_IDS`), never the object's
 * key order (R4.2–R4.3). "Changed" covers every way an entry differs — a pill naming one kind of
 * change (*moved*) would misstate an entry that was moved and renamed.
 */
export const changeSummary = (comparison: PlanComparison): ChangePill[] => {
  const pills: ChangePill[] = [];
  for (const step of PLAN_STEP_IDS) {
    if (!comparison.byStep[step] || comparison.byStep[step].total === 0) continue;
    const parts: string[] = [];
    for (const kind of KIND_ORDER) {
      if (PLAN_KIND_STEP[kind] !== step) continue;
      const k = comparison[kind];
      if (k.added.length) parts.push(phrase(k.added.length, kind, 'added'));
      if (k.removed.length) parts.push(phrase(k.removed.length, kind, 'removed'));
      if (k.changed.length) parts.push(phrase(k.changed.length, kind, 'changed'));
    }
    if (parts.length) pills.push({ step, label: STEP_TITLE[step], text: parts.join(' · ') });
  }
  return pills;
};

/** *v{N}* for a launch with a number, or `null`. */
export const launchVersion = (launch: Pick<TwinLaunchSummary, 'launchNumber'>): string | null =>
  launch.launchNumber == null ? null : `v${launch.launchNumber}`;

/*
 * ---- The Load confirmation (`twin-details-page-load-launch`) ----
 */

/** Each plan step's glyph in the difference rows (R2.4). */
const STEP_ICON: Record<PlanStepId, IconName> = {
  assets: 'dumbbell',
  plan: 'layer-group',
  cameras: 'camera',
  observers: 'eye',
  compositeMeasures: 'calculator',
};

/** Floors named in each direction before the rest are counted (R2.3). */
export const FLOOR_NAME_CAP = 3;

export interface DifferenceRow {
  step: PlanStepId;
  /** The step's title, as the step list names it. */
  label: string;
  icon: IconName;
  /** What the launch lacks, then what it has extra, then what changed, e.g. *22 fewer assets*. */
  text: string;
}

/** The floors named, at most `FLOOR_NAME_CAP`, then the rest counted; a blank name is `null`. */
const floorParts = (
  labels: string[],
  name: (floor: string | null) => string,
  more: (n: number) => string,
): string[] => {
  const named = labels.slice(0, FLOOR_NAME_CAP).map(label => name(label.trim() ? label : null));
  const rest = labels.length - FLOOR_NAME_CAP;
  return rest > 0 ? [...named, more(rest)] : named;
};

/**
 * What an earlier launch lacks, then has, against production, one row per step in the wizard's
 * order (R2.2–R2.4). `comparison` is `comparePlans(production, launch)`: its *removed* is what the
 * launch lacks, its *added* what only the launch has. Lacking comes first in every row (Maria,
 * 2026-10-25), and a kind with both sides says both — a net figure would hide one of them.
 */
export const launchDifference = (comparison: PlanComparison): DifferenceRow[] => {
  const rows: DifferenceRow[] = [];
  for (const step of PLAN_STEP_IDS) {
    const kinds = KIND_ORDER.filter(kind => PLAN_KIND_STEP[kind] === step);
    const lacking: string[] = [];
    const extra: string[] = [];
    const changed: string[] = [];
    for (const kind of kinds) {
      const { added, removed, changed: differs } = comparison[kind];
      if (kind === 'floors') {
        lacking.push(
          ...floorParts(
            removed.map(e => e.label),
            name => (name ? `No ${name} floor` : 'No unnamed floor'),
            n => `${n} more ${noun(n, kind)} missing`,
          ),
        );
        extra.push(
          ...floorParts(
            added.map(e => e.label),
            name =>
              name ? `${name} floor not in production` : 'An unnamed floor not in production',
            n => `${n} more ${noun(n, kind)} not in production`,
          ),
        );
      } else if (removed.length && added.length) {
        lacking.push(`${removed.length} ${noun(removed.length, kind)} missing`);
        extra.push(`${added.length} ${noun(added.length, kind)} not in production`);
      } else if (removed.length) {
        lacking.push(`${removed.length} fewer ${noun(removed.length, kind)}`);
      } else if (added.length) {
        extra.push(`${added.length} more ${noun(added.length, kind)}`);
      }
      if (differs.length) changed.push(`${differs.length} ${noun(differs.length, kind)} changed`);
    }
    const parts = [...lacking, ...extra, ...changed];
    if (parts.length)
      rows.push({ step, label: STEP_TITLE[step], icon: STEP_ICON[step], text: parts.join(' · ') });
  }
  return rows;
};

type LoadTarget = Pick<
  TwinLaunchSummary,
  'launchNumber' | 'launchedAt' | 'completedAt' | 'launchedByEmail'
>;

/** The confirmation's title (R1.1). */
export const loadTitle = (launch: LoadTarget): string => {
  const version = launchVersion(launch);
  return version
    ? `Load ${version} as the draft?`
    : `Load the launch of ${whenText(launch.completedAt ?? launch.launchedAt)} as the draft?`;
};

/**
 * The first paragraph (R1.2): when and by whom the launch was made, and what customers keep seeing.
 * `date` is the Versions table's date of the launch; `productionVersion` null says *production*.
 * With no launcher's email there is no *by* clause — it is never guessed, nor given to the reader.
 */
export const loadLead = (args: {
  launch: LoadTarget;
  date: string;
  productionVersion: string | null;
  currentEmail: string | null | undefined;
}): string => {
  const { launch, date, productionVersion, currentEmail } = args;
  const email = launch.launchedByEmail?.trim() || null;
  const who = !email ? null : isCurrentUser(email, currentEmail) ? 'you' : email;
  const tail = `${date ? ` on ${date}` : ''}${who ? ` by ${who}` : ''}.`;
  const version = launchVersion(launch);
  const made = version ? `${version} was launched${tail}` : `This launch was made${tail}`;
  return `${made} Customers keep seeing ${productionVersion ?? 'production'} until you launch.`;
};

/** The caption over the rows (R2.1). */
export const differenceCaption = (
  launch: Pick<TwinLaunchSummary, 'launchNumber'>,
  productionVersion: string | null,
): string => {
  const version = launchVersion(launch);
  return version && productionVersion
    ? `Compared with production (${productionVersion}), ${version} has`
    : 'Compared with production, this launch has';
};

/** The line for a launch with no difference from production (R2.5). */
export const noDifferenceText = (launch: Pick<TwinLaunchSummary, 'launchNumber'>): string =>
  `${launchVersion(launch) ?? 'This launch'} has the same floors, equipment, cameras, observers and measures as production.`;

/** The status line while the difference is worked out, or after it failed (Failure States). */
export const differenceStatusText = (
  launch: Pick<TwinLaunchSummary, 'launchNumber'>,
  state: 'loading' | 'failed',
): string => {
  const version = launchVersion(launch) ?? 'this launch';
  return state === 'loading'
    ? `Working out what ${version} lacks…`
    : `We couldn't work out what ${version} lacks compared with production.`;
};

/** The confirm button (R4.1). */
export const loadButtonText = (launch: Pick<TwinLaunchSummary, 'launchNumber'>): string => {
  const version = launchVersion(launch);
  return version ? `Load ${version} as Draft` : 'Load as Draft';
};

const parses = (iso: string | null | undefined): iso is string =>
  !!iso && !Number.isNaN(new Date(iso).getTime());

/**
 * The note about the current draft (R3.1). The count is said only from a comparison that was read —
 * while it is read, or after it failed, the sentence has no number, never *0 changes*.
 */
export const draftDropText = (
  changes: ChangesState | null,
  editedAt: string | null | undefined,
): string => {
  const restore = 'You can restore it from Versions until the next launch.';
  if (changes?.state !== 'ready') return `Your draft is dropped. ${restore}`;
  const n = changes.total;
  if (n === 0)
    return "Your draft has no changes from production. It's kept as the dropped draft until the next launch.";
  const since = parses(editedAt) ? ` since ${whenText(editedAt)}` : '';
  return `Your draft (${n} ${n === 1 ? 'change' : 'changes'}${since}) is dropped. ${restore}`;
};

/**
 * Launch Twin's sentence on where the draft comes from (R6.1), from the same origin the chip names,
 * so the two always agree; null where the chip names no launch (a new or migrating twin, an unread
 * history).
 */
export const launchDraftLine = (origin: DraftOrigin | undefined): string | null => {
  if (origin === undefined || origin === 'not-launched' || origin === 'unknown') return null;
  return `You’re launching your draft from ${origin === null ? 'an earlier launch' : `v${origin}`}.`;
};
